import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  User, Users, Briefcase, Handshake, Plus, Search, Mail, Phone,
  FileSignature, Clock, Euro, CheckCircle, XCircle, AlertCircle,
  Loader2, Eye, MoreHorizontal, Send, Percent
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const MEMBER_TYPES = {
  creative: {
    label: 'Créatif',
    description: 'Membre récurrent avec contrat',
    icon: User,
    color: 'bg-blue-100 text-blue-700'
  },
  subcontractor: {
    label: 'Sous-traitant',
    description: 'Tâches au forfait ponctuel',
    icon: Briefcase,
    color: 'bg-purple-100 text-purple-700'
  },
  affiliate: {
    label: 'Apporteur',
    description: 'Agent commercial - commission sur leads',
    icon: Handshake,
    color: 'bg-orange-100 text-orange-700'
  }
};

const TeamMembersPage = () => {
  const navigate = useNavigate();
  const { type: urlType } = useParams(); // 'members', 'subcontractors', 'affiliates'
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Determine type from URL path
  const pathname = window.location.pathname;
  const type = pathname.includes('/team/subcontractors') ? 'subcontractors' : urlType;
  
  // New member modal
  const [showNewMemberModal, setShowNewMemberModal] = useState(false);
  const [newMember, setNewMember] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    member_type: type === 'affiliates' ? 'affiliate' : type === 'subcontractors' ? 'subcontractor' : 'creative',
    assigned_role: 'team_member', // Role assignment: project_manager, customer_service, team_member, subcontractor
    hourly_rate: '',
    commission_rate: '10', // For affiliates
    notes: ''
  });
  const [creating, setCreating] = useState(false);

  // Available roles for team members
  const ROLE_OPTIONS = [
    { value: 'project_manager', label: 'Chef de Projet', description: 'Gestion des projets et de l\'équipe' },
    { value: 'customer_service', label: 'Service Client', description: 'Support et relation client' },
    { value: 'team_member', label: 'Membre / Créatif', description: 'Exécution des tâches' },
    { value: 'subcontractor', label: 'Sous-traitant', description: 'Missions ponctuelles' }
  ];

  // Determine current member type from URL
  const currentType = type === 'affiliates' ? 'affiliate' : type === 'subcontractors' ? 'subcontractor' : 'creative';
  const typeConfig = MEMBER_TYPES[currentType];

  useEffect(() => {
    fetchMembers();
  }, [type]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users/team-members');
      let data = response.data || [];
      
      // Filter by member type based on URL
      if (type === 'affiliates') {
        data = data.filter(m => m.member_type === 'affiliate');
      } else if (type === 'subcontractors') {
        data = data.filter(m => m.member_type === 'subcontractor');
      } else {
        // Default: show creatives (internal members)
        data = data.filter(m => !m.member_type || m.member_type === 'creative' || m.member_type === 'internal');
      }
      
      setMembers(data);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMember = async () => {
    if (!newMember.email || !newMember.first_name) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        ...newMember,
        role: newMember.assigned_role || 'team_member',
        member_type: currentType,
        hourly_rate: newMember.hourly_rate ? parseFloat(newMember.hourly_rate) : null,
        commission_rate: currentType === 'affiliate' ? parseFloat(newMember.commission_rate) : null
      };
      
      await api.post('/users/team-members', payload);
      toast.success(`${typeConfig.label} créé avec succès ! Un email d'invitation a été envoyé.`);
      setShowNewMemberModal(false);
      setNewMember({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        member_type: currentType,
        assigned_role: 'team_member',
        hourly_rate: '',
        commission_rate: '10',
        notes: ''
      });
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (member) => {
    if (member.contract_signed) {
      return <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>;
    } else if (member.contract_pending) {
      return <Badge className="bg-amber-100 text-amber-700">Contrat en attente</Badge>;
    } else if (member.profile_completed) {
      return <Badge className="bg-blue-100 text-blue-700">Profil complété</Badge>;
    } else {
      return <Badge className="bg-slate-100 text-slate-600">Invitation envoyée</Badge>;
    }
  };

  const filteredMembers = members.filter(m => {
    const matchesSearch = !searchQuery || 
      `${m.first_name} ${m.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && m.contract_signed) ||
      (filterStatus === 'pending' && !m.contract_signed);
    
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: members.length,
    active: members.filter(m => m.contract_signed).length,
    pending: members.filter(m => !m.contract_signed).length
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3" style={{ color: theme.colors?.primary }}>
              <typeConfig.icon className="w-7 h-7" />
              {type === 'affiliates' ? 'Apporteurs d\'affaires' : type === 'subcontractors' ? 'Sous-traitants' : 'Membres créatifs'}
            </h1>
            <p className="text-slate-500 mt-1">{typeConfig.description}</p>
          </div>
          <Button 
            onClick={() => setShowNewMemberModal(true)}
            style={{ backgroundColor: theme.colors?.accent }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter {currentType === 'affiliate' ? 'un apporteur' : currentType === 'subcontractor' ? 'un sous-traitant' : 'un membre'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", typeConfig.color.replace('text-', 'bg-').replace('-700', '-100'))}>
                  <typeConfig.icon className={cn("w-5 h-5", typeConfig.color.replace('bg-', 'text-').replace('-100', '-600'))} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
                  <p className="text-xs text-slate-500">Actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                  <p className="text-xs text-slate-500">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="active">Actifs</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Members List */}
        <Card>
          <CardContent className="p-0">
            {filteredMembers.length === 0 ? (
              <div className="text-center py-12">
                <typeConfig.icon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">Aucun {typeConfig.label.toLowerCase()} trouvé</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowNewMemberModal(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter le premier
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Contact</TableHead>
                    {currentType === 'affiliate' ? (
                      <TableHead className="text-center">Commission</TableHead>
                    ) : (
                      <TableHead className="text-center">Taux horaire</TableHead>
                    )}
                    <TableHead className="text-center">Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map(member => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar name={`${member.first_name} ${member.last_name}`} src={member.avatar_url} size="sm" />
                          <div>
                            <p className="font-medium">{member.first_name} {member.last_name}</p>
                            {member.company_name && (
                              <p className="text-xs text-slate-500">{member.company_name}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="flex items-center gap-1 text-slate-600">
                            <Mail className="w-3 h-3" />
                            {member.email}
                          </p>
                          {member.phone && (
                            <p className="flex items-center gap-1 text-slate-500">
                              <Phone className="w-3 h-3" />
                              {member.phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {currentType === 'affiliate' ? (
                          <span className="font-bold text-orange-600">{member.commission_rate || 10}%</span>
                        ) : (
                          <span className="font-bold" style={{ color: theme.colors?.accent }}>
                            {member.hourly_rate || '-'}€/h
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {getStatusBadge(member)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/team/member/${member.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir le profil
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/member-contracts?member=${member.id}`)}>
                              <FileSignature className="w-4 h-4 mr-2" />
                              Gérer le contrat
                            </DropdownMenuItem>
                            {!member.contract_signed && (
                              <DropdownMenuItem>
                                <Send className="w-4 h-4 mr-2" />
                                Renvoyer l'invitation
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* New Member Modal */}
        <Dialog open={showNewMemberModal} onOpenChange={setShowNewMemberModal}>
          <DialogContent className="max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle style={{ color: theme.colors?.primary }}>
                Ajouter {currentType === 'affiliate' ? 'un apporteur d\'affaires' : currentType === 'subcontractor' ? 'un sous-traitant' : 'un membre créatif'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prénom *</Label>
                  <Input
                    value={newMember.first_name}
                    onChange={(e) => setNewMember(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="Prénom"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={newMember.last_name}
                    onChange={(e) => setNewMember(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Nom"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemple.com"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={newMember.phone}
                  onChange={(e) => setNewMember(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+33 6 12 34 56 78"
                  className="mt-1"
                />
              </div>
              
              {/* Role assignment - only for non-affiliates */}
              {currentType !== 'affiliate' && (
                <div>
                  <Label>Rôle / Droits *</Label>
                  <Select
                    value={newMember.assigned_role}
                    onValueChange={(value) => setNewMember(prev => ({ ...prev, assigned_role: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Sélectionner un rôle" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          <div className="flex flex-col">
                            <span className="font-medium">{role.label}</span>
                            <span className="text-xs text-slate-500">{role.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Détermine les accès et permissions dans l'application
                  </p>
                </div>
              )}
              
              {currentType === 'affiliate' ? (
                <div>
                  <Label>Taux de commission</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      value={newMember.commission_rate}
                      onChange={(e) => setNewMember(prev => ({ ...prev, commission_rate: e.target.value }))}
                      placeholder="10"
                      className="w-24"
                    />
                    <span className="text-slate-500">%</span>
                    <span className="text-xs text-slate-400">sur le 1er projet du client apporté</span>
                  </div>
                </div>
              ) : (
                <div>
                  <Label>Taux horaire</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      value={newMember.hourly_rate}
                      onChange={(e) => setNewMember(prev => ({ ...prev, hourly_rate: e.target.value }))}
                      placeholder="35"
                      className="w-24"
                    />
                    <span className="text-slate-500">€ / heure</span>
                  </div>
                </div>
              )}
              
              <div>
                <Label>Notes internes</Label>
                <Textarea
                  value={newMember.notes}
                  onChange={(e) => setNewMember(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes visibles uniquement par l'agence..."
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Prochaines étapes :</strong>
                </p>
                <ol className="text-xs text-slate-500 mt-2 space-y-1 list-decimal list-inside">
                  <li>Un email d'invitation sera envoyé</li>
                  <li>Le {typeConfig.label.toLowerCase()} complète son profil (société, RIB)</li>
                  <li>Vous créez et envoyez le contrat</li>
                  <li>Le {typeConfig.label.toLowerCase()} signe et accède à son espace</li>
                </ol>
              </div>
            </div>
            
            <DialogFooter className="flex-shrink-0 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowNewMemberModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreateMember}
                disabled={creating || !newMember.email || !newMember.first_name}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer l'invitation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default TeamMembersPage;
